import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

export default function MusicaPage() {
  const [activeVerse, setActiveVerse] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark-900 via-dark-800 to-dark-900">
      {/* Header/Logo */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-dark-900/95 backdrop-blur-sm border-b border-gold-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/55de03a58447b22c9804f3000d5f2dfc/7f3918d052d8bace5fe919afd77c8449.png"
              alt="Cuaderno de La Magdalena"
              className="h-16 w-auto"
            />
          </a>
          <a
            href="/"
            className="flex items-center gap-2 text-warm-200 hover:text-gold-400 transition-colors cursor-pointer"
          >
            <i className="ri-home-line text-xl"></i>
            <span className="font-sans text-sm">Inicio</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Title Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="font-serif text-5xl md:text-7xl font-light text-gold-400 mb-4">
              La Magdalena
            </h1>
            <p className="font-sans text-2xl md:text-3xl text-warm-100">
              Johann Alvarez
            </p>
          </motion.div>

          {/* Video Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-20"
          >
            {/* Availability Notice */}
            <div className="mb-6 bg-gold-500/10 border-2 border-gold-500/30 rounded-xl p-6 text-center">
              <p className="font-sans text-lg text-gold-300 leading-relaxed">
                <span className="font-semibold">Video Disponible el 11 de Febrero a las 6PM.</span>
                <br />
                Puedes verlo ahora suscribiéndote como <span className="font-semibold">Subscriptor Abono</span> en mi canal{' '}
                <a 
                  href="https://www.youtube.com/@JohannAlvarezMusica" 
                  target="_blank" 
                  rel="nofollow noopener noreferrer"
                  className="text-gold-400 hover:text-gold-300 underline cursor-pointer"
                >
                  Johann Alvarez Musica
                </a>
                {' '}en Youtube.
              </p>
            </div>

            <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-2xl border-2 border-gold-500/30">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/zbyMVC1R0wg"
                title="LA MAGDALENA | JOHANN ALVAREZ"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              ></iframe>
            </div>
          </motion.div>

          {/* Lyrics Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-gold-500/20 shadow-xl"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-gold-400 text-center mb-12">
              Letra de la Canción
            </h2>

            <div className="max-w-3xl mx-auto space-y-10">
              {/* VERSE 1 */}
              <div
                className="group cursor-pointer transition-all duration-300"
                onClick={() => setActiveVerse(activeVerse === 1 ? null : 1)}
              >
                <h3 className="font-sans text-sm uppercase tracking-widest text-gold-500 mb-4 flex items-center gap-2">
                  <i className="ri-music-2-line"></i>
                  Verse 1
                </h3>
                <div className={`font-serif text-lg leading-relaxed ${activeVerse === 1 ? 'text-gold-300' : 'text-warm-100'} transition-colors`}>
                  <p className="mb-2">Librada de todo indulto,</p>
                  <p className="mb-2">ella siempre le seguía.</p>
                  <p className="mb-2">De la cruz hasta el sepulcro,</p>
                  <p className="mb-6">fiel permanecía.</p>
                  <p className="mb-2">Tu servir y entrega,</p>
                  <p className="mb-2">discípula del Mesías.</p>
                  <p className="mb-2">En las más duras pruebas,</p>
                  <p>desde allí su fe nacía.</p>
                </div>
              </div>

              {/* PRE-CHORUS */}
              <div
                className="group cursor-pointer transition-all duration-300"
                onClick={() => setActiveVerse(activeVerse === 2 ? null : 2)}
              >
                <h3 className="font-sans text-sm uppercase tracking-widest text-gold-500 mb-4 flex items-center gap-2">
                  <i className="ri-music-2-line"></i>
                  Pre-Chorus
                </h3>
                <div className={`font-serif text-lg leading-relaxed ${activeVerse === 2 ? 'text-gold-300' : 'text-warm-100'} transition-colors`}>
                  <p className="mb-2">Cuando todos se marchaban,</p>
                  <p className="mb-2">ella decidió quedarse.</p>
                  <p className="mb-2">No entendía cómo obraban,</p>
                  <p>pero eligió amarte.</p>
                </div>
              </div>

              {/* CHORUS */}
              <div
                className="group cursor-pointer transition-all duration-300 bg-gold-500/5 rounded-xl p-6 border-l-4 border-gold-500"
                onClick={() => setActiveVerse(activeVerse === 3 ? null : 3)}
              >
                <h3 className="font-sans text-sm uppercase tracking-widest text-gold-500 mb-4 flex items-center gap-2">
                  <i className="ri-heart-3-line"></i>
                  Chorus
                </h3>
                <div className={`font-serif text-xl leading-relaxed ${activeVerse === 3 ? 'text-gold-300' : 'text-gold-100'} transition-colors font-medium`}>
                  <p className="mb-2">Como a María Magdalena,</p>
                  <p className="mb-2">mujer que levantó de la arena,</p>
                  <p className="mb-2">Jesús rompió mis cadenas</p>
                  <p className="mb-6">y alivió mis penas.</p>
                  <p className="mb-2">Me llamó por mi nombre,</p>
                  <p className="mb-2">nunca me ha dejado.</p>
                  <p className="mb-2">Y cuando otros corren,</p>
                  <p>me quedo mejor a Su lado.</p>
                </div>
              </div>

              {/* VERSE 2 */}
              <div
                className="group cursor-pointer transition-all duration-300"
                onClick={() => setActiveVerse(activeVerse === 4 ? null : 4)}
              >
                <h3 className="font-sans text-sm uppercase tracking-widest text-gold-500 mb-4 flex items-center gap-2">
                  <i className="ri-music-2-line"></i>
                  Verse 2
                </h3>
                <div className={`font-serif text-lg leading-relaxed ${activeVerse === 4 ? 'text-gold-300' : 'text-warm-100'} transition-colors`}>
                  <p className="mb-2">Como ella yo cargué mis culpas,</p>
                  <p className="mb-2">también lloré a Sus pies.</p>
                  <p className="mb-2">Pensé que todo estaba perdido,</p>
                  <p className="mb-6">pero Él me vio otra vez.</p>
                  <p className="mb-2">Entre dudas y silencios,</p>
                  <p className="mb-2">Su mirada me sostuvo.</p>
                  <p className="mb-2">Donde todos vieron ruinas,</p>
                  <p>mi corazón Él sedujo.</p>
                </div>
              </div>

              {/* PRE-CHORUS 2 */}
              <div
                className="group cursor-pointer transition-all duration-300"
                onClick={() => setActiveVerse(activeVerse === 5 ? null : 5)}
              >
                <h3 className="font-sans text-sm uppercase tracking-widest text-gold-500 mb-4 flex items-center gap-2">
                  <i className="ri-music-2-line"></i>
                  Pre-Chorus 2
                </h3>
                <div className={`font-serif text-lg leading-relaxed ${activeVerse === 5 ? 'text-gold-300' : 'text-warm-100'} transition-colors`}>
                  <p className="mb-2">Él no le teme a mis caídas,</p>
                  <p className="mb-2">nunca me ha dejado atrás.</p>
                  <p className="mb-2">Cuando todos se apartaban,</p>
                  <p>Él por mí volvió a apostar.</p>
                </div>
              </div>

              {/* CHORUS (repeat indicator) */}
              <div className="bg-gold-500/5 rounded-xl p-6 border-l-4 border-gold-500">
                <h3 className="font-sans text-sm uppercase tracking-widest text-gold-500 mb-2 flex items-center gap-2">
                  <i className="ri-repeat-line"></i>
                  Chorus (se repite)
                </h3>
              </div>

              {/* BRIDGE */}
              <div
                className="group cursor-pointer transition-all duration-300 bg-warm-900/30 rounded-xl p-6 border-l-4 border-warm-100"
                onClick={() => setActiveVerse(activeVerse === 6 ? null : 6)}
              >
                <h3 className="font-sans text-sm uppercase tracking-widest text-warm-100 mb-4 flex items-center gap-2">
                  <i className="ri-sparkling-line"></i>
                  Bridge
                </h3>
                <div className={`font-serif text-xl leading-relaxed ${activeVerse === 6 ? 'text-warm-50' : 'text-warm-100'} transition-colors font-medium`}>
                  <p className="mb-2">La tumba está vacía,</p>
                  <p className="mb-2">Jesús resucitó.</p>
                  <p className="mb-2">Su amor venció la muerte,</p>
                  <p className="mb-6">y la historia reescribió.</p>
                  <p className="mb-2">Hoy nos toca a ti y a mí</p>
                  <p className="mb-2">salir y anunciar:</p>
                  <p className="mb-2">como discípulos vivir,</p>
                  <p>como cristianos amar.</p>
                </div>
              </div>

              {/* CHORUS FINAL */}
              <div
                className="group cursor-pointer transition-all duration-300 bg-gold-500/10 rounded-xl p-6 border-l-4 border-gold-500"
                onClick={() => setActiveVerse(activeVerse === 7 ? null : 7)}
              >
                <h3 className="font-sans text-sm uppercase tracking-widest text-gold-500 mb-4 flex items-center gap-2">
                  <i className="ri-heart-3-line"></i>
                  Chorus Final
                </h3>
                <div className={`font-serif text-xl leading-relaxed ${activeVerse === 7 ? 'text-gold-300' : 'text-gold-100'} transition-colors font-medium`}>
                  <p className="mb-2">Como a María Magdalena,</p>
                  <p className="mb-2">me levantó del polvo, de la arena.</p>
                  <p className="mb-2">No solo sanó mis heridas,</p>
                  <p className="mb-6">me confió Su mensaje y Su manera.</p>
                  <p className="mb-6 italic">Su manera de amar</p>
                  <p className="mb-2">Me llamó por mi nombre,</p>
                  <p className="mb-2">nunca me ha dejado.</p>
                  <p className="mb-2">Y cuando otros corren,</p>
                  <p className="mb-2">me quedo mejor a Su lado.</p>
                  <p className="italic">me quedo a Su lado.</p>
                </div>
              </div>

              {/* OUTRO */}
              <div
                className="group cursor-pointer transition-all duration-300"
                onClick={() => setActiveVerse(activeVerse === 8 ? null : 8)}
              >
                <h3 className="font-sans text-sm uppercase tracking-widest text-gold-500 mb-4 flex items-center gap-2">
                  <i className="ri-music-2-line"></i>
                  Outro
                </h3>
                <div className={`font-serif text-lg leading-relaxed ${activeVerse === 8 ? 'text-gold-300' : 'text-warm-100'} transition-colors`}>
                  <p className="mb-2">Como a María Magdalena,</p>
                  <p className="mb-2">me levantó del polvo, de la arena.</p>
                  <p className="mb-2">Jesús rompió mis cadenas</p>
                  <p>y alivió mis penas.</p>
                </div>
              </div>
            </div>

            {/* Share/Listen Section */}
            <div className="mt-16 pt-8 border-t border-gold-500/20 text-center">
              <p className="font-sans text-warm-200 mb-6">Escucha "La Magdalena" en YouTube</p>
              <a
                href="https://youtu.be/zbyMVC1R0wg"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gold-500 text-dark-900 font-sans font-semibold rounded-lg hover:bg-gold-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-youtube-line text-2xl"></i>
                Ver en YouTube
              </a>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-20 text-center"
          >
            <h3 className="font-serif text-3xl text-gold-400 mb-6">
              Vive la Peregrinación Interior
            </h3>
            <p className="font-sans text-lg text-warm-200 mb-8 max-w-2xl mx-auto">
              Profundiza tu experiencia espiritual con el Cuaderno de La Magdalena y el Curso de 40 días.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="/#cuaderno"
                className="inline-flex items-center gap-2 px-8 py-3 bg-transparent border-2 border-gold-500 text-gold-400 font-sans font-semibold rounded-lg hover:bg-gold-500 hover:text-dark-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line text-xl"></i>
                Comprar Cuaderno
              </a>
              <a
                href="/#curso"
                className="inline-flex items-center gap-2 px-8 py-3 bg-transparent border-2 border-warm-100 text-warm-100 font-sans font-semibold rounded-lg hover:bg-warm-100 hover:text-dark-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-computer-line text-xl"></i>
                Inscribirme al Curso
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative bg-gradient-to-b from-dark-800 to-dark-900 border-t border-gold-500/20 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-sans text-sm text-warm-200">
              © 2025 Cuaderno de La Magdalena. Todos los derechos reservados.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="font-sans text-sm text-warm-200 hover:text-gold-400 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}